import type {ReactElement} from 'react';
import {useContext, useRef} from 'react';
import {StyleTile} from '@Components/style-tile/style-tile';
import styles from '@Components/mystuff-events-home/components/page-details-section/page-details-section.module.scss';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import {StylesInputLeftDrawer} from '@Components/styles-input-left-drawer/styles-input-left-drawer';
import {StylesLegibilityHint} from '@Components/styles-legibility-hint/styles-legibility-hint';
import {useDispatch} from 'react-redux';
import {
  getEventWizardBackgroundColor,
  getEventWizardContentColor,
  getEventWizardLegibilityStatus,
  getEventWizardStyleFont,
  useEventWizardIsStylesPanelOpen,
  useEventWizardColorToRestoreOnUndo,
  useEventWizardLegibilityStatus,
  useEventWizardStyles,
} from '@Components/event-wizard/event-wizard.hooks';
import {
  onEventWizardIsStylesPanelOpenUpdate,
  onEventWizardStyleFontUpdate,
  onEventWizardStyleLegibilityUpdate,
} from '@Components/event-wizard/event-wizard-slice';
import {applyEventWizardColorLegibility} from '@Components/event-wizard/event-wizard.helper';
import type {ColorLegibilityState} from '@Libraries/event-colors.library';
import {
  appliesCustomBackground,
  applyBackgroundSelection,
  applyContentSelection,
  applyLegibilityFix,
  applyLegibilityUndo,
  dismissLegibilityBanner,
  getLegibilityMemo,
} from '@Libraries/event-colors.library';
import {updateStyleFont} from '@Components/event-wizard/preview/preview.helper';
import {Text, TextSize} from '@Components/text';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {openStylesInputModal} from '@Modals/styles-input-modal/styles-input-modal.library';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';
import type {GAEventsMap, LegibilityState} from '@Panels/styles-input-panel/styles-input-panel.types';
import {StylesInputPanelGAEventType} from '@Panels/styles-input-panel/styles-input-panel.types';

export function EventStyle(): ReactElement {
  const dispatch = useDispatch();
  const {isSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);

  const selectedStyle = useEventWizardStyles();
  const isStylesPanelOpen = useEventWizardIsStylesPanelOpen();

  const backgroundColor = selectedStyle.colors.background ?? null;
  const contentColor = backgroundColor !== null ? selectedStyle.colors.dark : selectedStyle.colors.primary;
  const legibilityStatus = useEventWizardLegibilityStatus();
  const colorToRestoreOnUndo = useEventWizardColorToRestoreOnUndo();

  /**
   * The colours currently on the page, in the shape the legibility machine takes.
   *
   * Held in a ref as well as read from Redux because these handlers are also handed to the
   * small-screen styles modal, which stores its props once when it opens. Without the ref every
   * interaction after the first would transition from the colours that were current at open time.
   */
  const legibilityState: ColorLegibilityState = {
    background: backgroundColor,
    contentColor,
    colorToRestoreOnUndo,
    status: legibilityStatus,
  };
  const legibilityStateRef = useRef(legibilityState);
  legibilityStateRef.current = legibilityState;

  const openStylesPanel = (): void => {
    if (isSmallScreen) {
      // The small-screen path renders the same panel inside a modal, so it must receive the same
      // background row and legibility banner — without them the custom-background feature is simply
      // unreachable on a phone.
      openStylesInputModal({
        allowImageSelection: false,
        selectedColorHex: contentColor,
        contentColorLabelKey: 'pmwjs_content_color',
        showBackgroundColorRow: true,
        selectedBackgroundColorHex: backgroundColor ?? PMW_COLORS_NEUTRAL.NEUTRAL_0,
        onSelectBackgroundColorHex,
        onApplyColorPair,
        legibility: legibilityBanner,
        selectedFontFamily: selectedStyle.fontFamily,
        getColorReduxSelector: getEventWizardContentColor,
        getBackgroundColorReduxSelector: getEventWizardBackgroundColor,
        getLegibilityStatusReduxSelector: getEventWizardLegibilityStatus,
        getFontReduxSelector: getEventWizardStyleFont,
        onSelectColorHex,
        onSelectFontFamily,
        gaEventsMap: getGA4EventsMap(),
      });
      return;
    }
    dispatch(onEventWizardIsStylesPanelOpenUpdate(true));
  };

  const closeStylesPanel = (): void => {
    dispatch(onEventWizardIsStylesPanelOpenUpdate(false));
  };

  const onSelectColorHex = (newContent: string): void => {
    applyEventWizardColorLegibility(applyContentSelection(legibilityStateRef.current, newContent));
  };

  const onSelectBackgroundColorHex = (pickedBackground: string): void => {
    applyEventWizardColorLegibility(applyBackgroundSelection(legibilityStateRef.current, pickedBackground));

    if (appliesCustomBackground(pickedBackground)) {
      trackGA4CustomEvent(GA4EventName.EVENT_WIZARD_APPLY_BACKGROUND);
    }
  };

  const onUndoLegibility = (): void => {
    applyEventWizardColorLegibility(applyLegibilityUndo(legibilityStateRef.current));
  };

  const onFixLegibility = (): void => {
    applyEventWizardColorLegibility(applyLegibilityFix(legibilityStateRef.current));
  };

  const onDismissLegibility = (): void => {
    dispatch(onEventWizardStyleLegibilityUpdate(getLegibilityMemo(dismissLegibilityBanner(legibilityStateRef.current))));
  };

  /** Brand kit: the content color and the background arrive together, so they apply as one step. */
  const onApplyColorPair = (contentHex: string, backgroundHex: string): void => {
    const seeded = applyContentSelection(legibilityStateRef.current, contentHex);
    applyEventWizardColorLegibility(applyBackgroundSelection(seeded, backgroundHex));
  };

  const onSelectFontFamily = (newFont: string): void => {
    dispatch(onEventWizardStyleFontUpdate(newFont));
    updateStyleFont(newFont);
  };

  const getGA4EventsMap = (): GAEventsMap => {
    return {
      [StylesInputPanelGAEventType.BRAND_KIT]: GA4EventName.EVENTS_APPLY_BRAND_KIT,
      [StylesInputPanelGAEventType.COLOR]: GA4EventName.EVENT_WIZARD_APPLY_COLOR,
      [StylesInputPanelGAEventType.FONT]: GA4EventName.EVENT_WIZARD_APPLY_FONT,
    };
  };

  /**
   * The three swatches on the styles tile. Mode B shows what the page actually uses — accent,
   * page background, page text. Mode A must show the design-system neutrals it did before this
   * feature: there the derived `dark` is not the page text (the page uses the DS neutral), so
   * putting it on the tile advertised a dark brand shade the page never renders.
   */
  const getStyleTileColors = (): string[] => {
    if (backgroundColor === null) {
      return [selectedStyle.colors.primary, PMW_COLORS_NEUTRAL.NEUTRAL_0, PMW_COLORS_NEUTRAL.NEUTRAL_70];
    }
    return [selectedStyle.colors.primary, backgroundColor, selectedStyle.colors.dark];
  };

  const legibilityBanner: LegibilityState = {
    status: legibilityStatus,
    onUndo: onUndoLegibility,
    onFix: onFixLegibility,
    onDismiss: onDismissLegibility,
  };

  return (
    <div>
      <div className="flex-row-align-center spacing-m-b-1">
        <Text val={window.i18next.t('pmwjs_styles')} size={TextSize.XXSMALL} bold />
      </div>
      <StyleTile
        tileText={window.i18next.t('pmwjs_default')}
        className={styles.styleTile}
        isLoading={false}
        showImage={false}
        colorHexes={getStyleTileColors()}
        fontFamily={selectedStyle.fontFamily}
        onClick={openStylesPanel}
      />

      <StylesLegibilityHint status={legibilityStatus} onClick={openStylesPanel} onFix={onFixLegibility} className="spacing-m-t-2" />

      <StylesInputLeftDrawer
        isOpen={isStylesPanelOpen}
        allowImageSelection={false}
        selectedColorHex={contentColor}
        onSelectColorHex={onSelectColorHex}
        contentColorLabelKey="pmwjs_content_color"
        showBackgroundColorRow
        selectedBackgroundColorHex={backgroundColor ?? PMW_COLORS_NEUTRAL.NEUTRAL_0}
        onSelectBackgroundColorHex={onSelectBackgroundColorHex}
        onApplyColorPair={onApplyColorPair}
        legibility={legibilityBanner}
        selectedFontFamily={selectedStyle.fontFamily}
        onSelectFontFamily={onSelectFontFamily}
        onClose={closeStylesPanel}
        gaEventsMap={getGA4EventsMap()}
      />
    </div>
  );
}
